import { getApiErrorContext, isClientTimeout } from './api.js';
import { serverIssues } from './serverFieldErrors.js';

/**
 * Maps known API error codes to plain-English messages we can surface
 * in toasts and inline form errors. Falls back to the server-provided
 * message (or a transport-aware generic) when the code isn't on the
 * list. Auth pages keep using `getAuthErrorContext` directly because
 * they need typed `unlockAt` details — this helper is the catch-all
 * for everything else.
 */
const CODE_MAP: Record<string, string> = {
  UNAUTHENTICATED: 'Your session has ended. Please sign in again.',
  FORBIDDEN: "You don't have permission to do that.",
  ACCOUNT_LOCKED:
    'Account temporarily locked after too many failed sign-ins. Try again in a few minutes.',
  /* NOT "that link is no longer valid". `INVALID_TOKEN` is thrown for every
     token this API holds — the access JWT, the REFRESH token, a reset link, an
     install token — and the two link cases have their own screens, which read
     `getAuthErrorContext` directly rather than coming through here (see the
     note above). So the case that actually reaches this map is a dead SESSION:
     the client retries any 401 once through `/auth/refresh`, and when that
     refresh is itself rejected the original code surfaces here.
     It was telling a reader whose session had timed out — on the profile, on a
     form, anywhere — that a link they never followed had expired, and to
     request a new one of something that does not exist. */
  INVALID_TOKEN: 'Your session has ended. Please sign in again.',
  EXPIRED_TOKEN: 'That link has expired.',
  VALIDATION_ERROR: 'Some fields need attention.',
  DUPLICATE_ENTRY: 'That value is already in use. Please choose another.',
  NOT_FOUND: "We couldn't find what you were looking for.",
  RATE_LIMITED: 'Too many requests. Please wait a minute and try again.',
  // The device approval gate. The login page renders its own copy for these two
  // (a pending device is informational, not an error) — these are the fallback
  // for anywhere else a 403 from the gate could surface, e.g. a toast.
  DEVICE_PENDING_APPROVAL:
    'This device is waiting for administrator approval. Try again once it has been approved.',
  DEVICE_NOT_APPROVED:
    'This device is not allowed to sign in. Contact your administrator if you think that is wrong.',
  OUTSIDE_GEOFENCE:
    "You're not at the worksite. Move closer to the office, or ask your manager to punch you in.",
  IP_NOT_ALLOWED:
    "Punching from this network isn't allowed. Please use the office Wi-Fi.",
  MOCK_LOCATION:
    'Your phone is reporting a fake location. Disable any GPS-spoofing app and try again.',
  LOW_GPS_ACCURACY:
    'GPS signal is too weak right now. Step into an open area and try again.',
  INTERNAL_ERROR:
    'Something went wrong on our end. Please try again, or contact support if it keeps happening.',
};

/**
 * The message the API's zod middleware attaches to every schema failure. It
 * always travels WITH `details.fields`, so on its own it tells the user
 * nothing — that's the one case where the generic CODE_MAP copy reads better.
 */
const GENERIC_VALIDATION_MESSAGE = 'Request validation failed';

/** `ApiError.forbidden()`'s default — a 403 that gives no reason of its own. */
const SERVER_GENERIC_FORBIDDEN = 'You do not have permission to perform this action';

export function friendlyMessage(err: unknown): string {
  const ctx = getApiErrorContext(err);

  /* A TIMED-OUT WRITE MAY STILL LAND (audit F-12). "Couldn't reach the
     server" told the operator nothing was saved while the server went on
     saving, and a retry then skipped most of it as already done. */
  if (isClientTimeout(err)) {
    return 'The server is taking longer than expected and may still be saving. Wait a minute, then check what was saved.';
  }

  // Transport / offline detection — when there's no HTTP status and the
  // code didn't get parsed, assume the request didn't make it out.
  if (ctx.status === undefined && ctx.code === 'UNKNOWN') {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return "You're offline. Reconnect and try again.";
    }
    return "Couldn't reach the server. Check your connection and try again.";
  }

  /* 413 NEVER COMES FROM THIS PRODUCT, so it needs its own sentence.
   *
   * Every upload route sets its own multer ceiling and refuses over-size files
   * in this API's own words with a 400 (`fieldReleases.routes.ts` says so for
   * the APK, at a 150 MB limit). A 413 therefore always came from a hop in
   * FRONT of the API — a reverse proxy or CDN with a body-size cap — which
   * answers in HTML, carries no error code, and reached the operator as axios's
   * raw "Request failed with status code 413".
   *
   * That sentence sends the wrong person to fix the wrong thing: it reads as a
   * fault in the file, and the operator's next move is to try a smaller one,
   * which will fail identically. The obstacle is a server setting and the
   * person who can move it is whoever runs the box (§3.11). */
  if (ctx.status === 413) {
    return (
      'The server refused this upload as too large before the app saw it. ' +
      'This is a size cap on the web server or CDN in front of the API, not a ' +
      'limit in Hadir. ' +
      'Ask whoever administers the server to raise the request body limit.'
    );
  }

  /* A 403 THAT EXPLAINS ITSELF KEEPS ITS SENTENCE. The server refuses for
     different reasons — a missing permission, "You must change your password
     before continuing.", "Account is not active", a salary refusal naming
     Settings → Roles & access — and the generic line above used to replace
     every one of them. An administrator then saw "You don't have permission"
     on every screen with nothing saying why (owner, 2026-10-03). Only the
     server's own generic refusal keeps the generic line. */
  const explainedRefusal =
    // A bare status word ("Forbidden") is not a reason either.
    ctx.code === 'FORBIDDEN' && /\s/.test(ctx.message.trim()) && ctx.message !== SERVER_GENERIC_FORBIDDEN
      ? ctx.message
      : undefined;
  const base = explainedRefusal ?? CODE_MAP[ctx.code] ?? ctx.message;

  if (ctx.code === 'VALIDATION_ERROR') {
    // Both refusal shapes — the zod middleware's `details: [...]` and a
    // hand-built `details.fields` — read through the one parser the forms use.
    const bullets = serverIssues(err)
      .slice(0, 3)
      .map(({ path, message }) => `${path}: ${message}`);
    if (bullets.length > 0) {
      return `${base}\n• ${bullets.join('\n• ')}`;
    }

    // No field details. `base` is the generic form copy from CODE_MAP, which
    // is right for a rejected form but wrong for the many VALIDATION_ERRORs
    // that aren't about a form at all — an export refusing because no WPS
    // establishment ID is configured, or because the month has no payable
    // records. Those arrive with a specific, actionable server message and
    // "Some fields need attention" actively misdirects the operator, who has
    // no form in front of them. Prefer the server's own wording whenever it
    // isn't the bare zod wrapper.
    if (ctx.message && ctx.message !== GENERIC_VALIDATION_MESSAGE) {
      return ctx.message;
    }
  }

  return base;
}
